import { StreamFrame } from '~/components/chat/message'
import type { ContextUploadApiItem } from '~/types/contextUpload'
import { ConversationWithTasks } from '~/types/Conversations'
import { fetchWrapper } from '~/utils/fetchWrapper'
import { extractApiError } from '~/utils/shared'

// Re-export the shared type for consumers
export type { CanvasContextInfo } from '~/types/canvas'
import type { CanvasContextInfo } from '~/types/canvas'

export const DEFAULT_CHAT_EXECUTION_MODE = 'agent'
export const DEFAULT_CHAT_VERIFICATION_MODE = 'verify'

interface BasePayload {
  workspaceUuid: string
  conversationUuid: string
  signal?: AbortSignal
}

interface ChatPayload extends BasePayload {
  message: string
  executionMode?: string
  verificationMode?: string
  contextUpload?: ContextUploadApiItem[]
  canvasContextInfo?: CanvasContextInfo[]
  isSpeechInput?: boolean
}

export async function* chatStream(payload: ChatPayload) {
  const {
    workspaceUuid,
    conversationUuid,
    message,
    executionMode,
    verificationMode,
    contextUpload,
    canvasContextInfo,
    isSpeechInput,
    signal
  } = payload
  const chat_api_path = `/api/workspace/${workspaceUuid}/conversation/${conversationUuid}/chat`

  let reader: ReadableStreamDefaultReader<Uint8Array> | null = null
  let response: Response | null = null

  // Convert canvasContextInfo array to snake_case for API
  const canvasContextInfoApi = canvasContextInfo?.map(ctx => ({
    canvas_hash: ctx.canvasHash,
    canvas_title: ctx.canvasTitle,
    context_text: ctx.contextText,
    context_before: ctx.contextBefore,
    context_after: ctx.contextAfter,
    context_start_line: ctx.contextStartLine,
    context_end_line: ctx.contextEndLine
  }))

  try {
    response = await fetchWrapper(chat_api_path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        message,
        execution_mode: executionMode ?? DEFAULT_CHAT_EXECUTION_MODE,
        verification_mode: verificationMode ?? DEFAULT_CHAT_VERIFICATION_MODE,
        context_upload: contextUpload,
        canvas_context_info: canvasContextInfoApi,
        is_speech_input: isSpeechInput || undefined
      }),
      signal,
      isStreaming: true,
      timeoutMs: 120000 // 2 minutes for streaming
    })

    if (!response.ok) {
      let parsed = {
        message: `Failed to fetch: ${response.statusText}`,
        errorType: undefined as string | undefined
      }
      try {
        const errorJson = JSON.parse(await response.text())
        parsed = extractApiError(errorJson, parsed.message)
      } catch {
        // Fallback to statusText if we can't parse the error response
      }
      const error = new Error(parsed.message) as Error & { errorType?: string }
      error.errorType = parsed.errorType
      throw error
    }

    reader = response.body?.getReader() ?? null
    if (!reader) {
      throw new Error('Response body is not readable')
    }
    const decoder = new TextDecoder()

    let json_buffer = ''
    while (true) {
      const { done, value } = await reader.read()
      if (done) break

      const decodedValue = decoder.decode(value)
      const parts = decodedValue.split('\n\ndata: ')

      for (const part of parts) {
        if (part.trim()) {
          const json = part.replace(/^data: /, '').trim()

          json_buffer += json
          if (!json_buffer.endsWith('}')) {
            continue
          }

          const chunk: StreamFrame = JSON.parse(json_buffer)
          json_buffer = ''
          const { event, frame_id, chunk_id, chunk_total, data } = chunk
          yield { frame_id, event, data, chunk_id, chunk_total }
        }
      }
    }
  } catch (error: unknown) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      return
    } else {
      throw error
    }
  } finally {
    // Server-side Connection: close header handles all connection cleanup
    // Just release reader lock as good practice
    if (reader) {
      reader.releaseLock()
    }
  }
}

interface RepositoryBranch {
  conn_type: string
  repository: string
  branch: string
}

interface WorkspaceChatStartParams {
  mode?: string
  repository_branches?: RepositoryBranch[]
  s3_upload_paths?: string[] // S3 paths for uploaded repositories
  integration_platform?: string // IntegrationPlatform enum name
  agent_mode?: string // 'agent' or 'ask' (deprecated, use execution_mode)
  speed_mode?: string // 'fast' or 'verify' (deprecated, use verification_mode)
  execution_mode?: string // 'ask' or 'agent'
  verification_mode?: string // 'fast' or 'verify'
}

/**
 * Start a new conversation from workspace home page
 *
 * Backend endpoint: POST /api/workspace/{workspace_uuid}/chat/start
 * Request body: { mode?, repository_branches? }
 *
 * The backend will set the conversation title to the mode name
 * (e.g., "Advanced Q&A Agent", "DataWeave Generator", "Repository Coder")
 *
 * The frontend should then navigate to the conversation view and provide the first prompt
 * through URL `initial_message` or sessionStorage handoff, which triggers the actual chat
 * streaming through the normal chat endpoint.
 *
 * Returns: ConversationWithTasks object with full conversation details (including title)
 */
export async function workspaceChatStart(
  workspaceUuid: string,
  params: WorkspaceChatStartParams
): Promise<ConversationWithTasks> {
  const chat_start_api_path = `/api/workspace/${workspaceUuid}/chat/start`

  const response = await fetchWrapper(chat_start_api_path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      ...params,
      execution_mode: params.execution_mode ?? params.agent_mode ?? DEFAULT_CHAT_EXECUTION_MODE,
      verification_mode:
        params.verification_mode ?? params.speed_mode ?? DEFAULT_CHAT_VERIFICATION_MODE
    })
  })

  if (!response.ok) {
    let parsed = {
      message: `Failed to fetch: ${response.statusText}`,
      errorType: undefined as string | undefined
    }
    try {
      const errorJson = JSON.parse(await response.text())
      parsed = extractApiError(errorJson, parsed.message)
    } catch {
      // Fallback to statusText if we can't parse the error response
    }
    const error = new Error(parsed.message) as Error & { errorType?: string }
    error.errorType = parsed.errorType
    throw error
  }

  const conversation = await response.json()
  return conversation
}

export interface ProgressMessageResponse {
  taskStatus: string
  messageType: string
  title: string
  internalMsgs: string[]
  toolOutputs: string[]
  checkpointMsg: string
  msgHash: string
  createdTsNs: number
}

/**
 * Fetch progress messages for a specific task
 *
 * Backend endpoint: GET /api/workspace/{workspace_uuid}/conversation/{conversation_uuid}/task/{task_id}/progress-messages
 *
 * Returns deduplicated messages (by msg_hash) for UI display
 */
export async function getTaskProgressMessages(
  workspaceUuid: string,
  conversationUuid: string,
  taskId: number
): Promise<ProgressMessageResponse[]> {
  const api_path = `/api/workspace/${workspaceUuid}/conversation/${conversationUuid}/task/${taskId}/progress-messages`

  const response = await fetchWrapper(api_path, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' }
  })

  if (!response.ok) {
    let errorMessage = `Failed to fetch: ${response.statusText}`
    try {
      const errorJson = JSON.parse(await response.text())
      errorMessage = extractApiError(errorJson, errorMessage).message
    } catch {
      // Fallback to statusText if we can't parse the error response
    }
    throw new Error(errorMessage)
  }

  const data = await response.json()
  return data.progress_messages || []
}
